import { NS, POSITIVE_ACTIONS, NEGATIVE_ACTIONS } from '@ownyou/shared-types';

/** Minimal MemoryStore interface — matches the subset of methods used here */
export interface EngagementMemoryStore {
  put<T>(namespace: readonly string[], key: string, value: T): Promise<void>;
  get<T>(namespace: readonly string[], key: string): Promise<T | null>;
}

export interface EngagementEvent {
  id: string;
  timestamp: number;
  missionId: string;
  entitySource?: string;
  entityCategory?: string;
  agentType: string;

  action:
    | 'shown'
    | 'accepted'
    | 'dismissed'
    | 'completed'
    | 'loved'
    | 'not_interested'
    | 'snoozed'
    | 'clicked_primary'
    | 'clicked_secondary'
    | 'refined'
    | 'emoji_positive'
    | 'emoji_negative';

  context?: {
    timeOfDay: string;
    dayOfWeek: number;
    sessionDuration: number;
    missionAge: number;
  };

  refinementMessage?: string;
  profileSignalExtracted?: string;
  feedbackType?: string;
  isComposite: boolean;
  isCascade: boolean;
  source: 'detection' | 'user_intent' | 'incremental' | 'cascade' | 'refresh';
}

export interface EngagementStats {
  totalEvents: number;
  actionCounts: Record<string, number>;
  categoryAcceptRates: Record<string, { accepted: number; dismissed: number; rate: number }>;
}

const MAX_EVENTS = 500;

// Re-export for backward compatibility (canonical definitions in @ownyou/shared-types)
export { POSITIVE_ACTIONS, NEGATIVE_ACTIONS };

export class EngagementLogger {
  /** Promise-chain mutex to serialize log() writes and prevent read-modify-write races */
  private writeLock: Promise<void> = Promise.resolve();

  constructor(
    private store: EngagementMemoryStore,
    private userId: string,
  ) {}

  private ns() {
    return NS.engagementEvents(this.userId);
  }

  async log(
    event: Omit<EngagementEvent, 'id' | 'timestamp' | 'context'> & {
      context?: Partial<EngagementEvent['context']>;
    },
  ): Promise<void> {
    // Serialize writes via promise chain to prevent concurrent read-modify-write races
    const release = this.writeLock;
    let resolve!: () => void;
    this.writeLock = new Promise<void>(r => { resolve = r; });

    try {
      await release;
      const existing = await this.store.get<{ events: EngagementEvent[] }>(this.ns(), 'events');
      const events = existing?.events ?? [];

      const newEvent: EngagementEvent = {
        ...event,
        id: crypto.randomUUID(),
        timestamp: Date.now(),
        context: {
          timeOfDay: getTimeOfDay(),
          dayOfWeek: new Date().getDay(),
          sessionDuration: event.context?.sessionDuration ?? 0,
          missionAge: event.context?.missionAge ?? 0,
        },
      };

      events.push(newEvent);
      const trimmed = events.length > MAX_EVENTS ? events.slice(events.length - MAX_EVENTS) : events;
      await this.store.put(this.ns(), 'events', { events: trimmed });
    } finally {
      resolve();
    }
  }

  async getRecent(limit: number = 50): Promise<EngagementEvent[]> {
    const data = await this.store.get<{ events: EngagementEvent[] }>(this.ns(), 'events');
    const events = data?.events ?? [];
    return events.slice(-limit);
  }

  async getByEntity(entitySource: string): Promise<EngagementEvent[]> {
    const data = await this.store.get<{ events: EngagementEvent[] }>(this.ns(), 'events');
    return (data?.events ?? []).filter((e) => e.entitySource === entitySource);
  }

  async getByCategory(category: string): Promise<EngagementEvent[]> {
    const data = await this.store.get<{ events: EngagementEvent[] }>(this.ns(), 'events');
    return (data?.events ?? []).filter((e) => e.entityCategory === category);
  }

  async getStats(): Promise<EngagementStats> {
    const data = await this.store.get<{ events: EngagementEvent[] }>(this.ns(), 'events');
    const events = data?.events ?? [];

    const actionCounts: Record<string, number> = {};
    const catAccepts: Record<string, { accepted: number; dismissed: number }> = {};

    for (const e of events) {
      actionCounts[e.action] = (actionCounts[e.action] ?? 0) + 1;

      if (e.entityCategory) {
        if (!catAccepts[e.entityCategory]) catAccepts[e.entityCategory] = { accepted: 0, dismissed: 0 };
        if (POSITIVE_ACTIONS.has(e.action)) catAccepts[e.entityCategory].accepted++;
        if (NEGATIVE_ACTIONS.has(e.action)) catAccepts[e.entityCategory].dismissed++;
      }
    }

    const categoryAcceptRates: Record<string, { accepted: number; dismissed: number; rate: number }> = {};
    for (const [cat, { accepted, dismissed }] of Object.entries(catAccepts)) {
      const total = accepted + dismissed;
      categoryAcceptRates[cat] = { accepted, dismissed, rate: total > 0 ? accepted / total : 0 };
    }

    return { totalEvents: events.length, actionCounts, categoryAcceptRates };
  }
}

function getTimeOfDay(): string {
  const hour = new Date().getHours();
  if (hour < 6) return 'late_night';
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  if (hour < 22) return 'evening';
  return 'late_night';
}
